import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader, SpecraSectionHeader, SpecraSourceCard } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { rfiQuestionHref } from '../../../components/RecordShell/rfiNav';
import { projectDataClient } from '../../../lib/connect/clients';
import { rfiSectionClient } from '../rfiSectionHooks';
import { useRfiRecord } from './RfiRecordContext';
import { useRfiAnswerSections } from './useRfiAnswerSections';
export const PAST_ANSWERS_KEY = 'rfi-past-answers';
/** `SearchFinalAnswers` (WRT-07 v2.2): cp0 search over approved answers. */ export function useRfiPastAnswers(rfiResponseProjectId) {
    return useQuery({
        queryKey: [
            PAST_ANSWERS_KEY,
            rfiResponseProjectId
        ],
        enabled: Boolean(rfiResponseProjectId),
        staleTime: 60000,
        retry: false,
        queryFn: ()=>rfiSectionClient.searchFinalAnswers({
                rfiResponseProjectId,
                sectionIds: [],
                perQuestionLimit: 3
            })
    });
}
/** Library narratives (no percentage), listed under the past answers. */ export function useLibraryNarratives(enabled) {
    return useQuery({
        queryKey: [
            'rfi-past-answers-library'
        ],
        enabled,
        staleTime: 60000,
        retry: false,
        queryFn: async ()=>(await projectDataClient.searchLibraryItems({
                filter: {
                    scope: 'discovery',
                    query: '',
                    itemType: 'narrative'
                },
                offset: 0,
                limit: 10
            })).items
    });
}
/**
 * Past answers (WRT-07 scope item 7): for each question, the closest approved
 * answers of the tenant's other RFIs, with cp0's score as "% similar". Each
 * opens that question page (read-only for people who cannot edit it there).
 */ export function RfiPastAnswersPage() {
    const record = useRfiRecord();
    const pangaeaId = record?.pangaeaId ?? '';
    const sections = useRfiAnswerSections(pangaeaId);
    const past = useRfiPastAnswers(pangaeaId);
    const library = useLibraryNarratives(Boolean(record));
    if (!record || sections.isLoading || past.isLoading) {
        return /*#__PURE__*/ _jsx(SpecraSpinner, {
            className: "m-[var(--space-6)]",
            label: "Loading past answers"
        });
    }
    const results = past.data?.results ?? [];
    const distinct = new Set(results.flatMap((r)=>r.matches.map((m)=>m.sectionId))).size;
    const unavailable = past.isError && (!(past.error instanceof ConnectError) || past.error.code !== Code.PermissionDenied);
    const numberOf = (sectionId)=>sections.sections.findIndex((s)=>s.id === sectionId) + 1;
    const withMatches = results.filter((r)=>r.matches.length > 0);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-4)] p-[var(--space-6)]",
        "data-testid": Testids.rfiWorkspace.pastAnswers,
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                meta: distinct > 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: `${distinct} approved ${distinct === 1 ? 'answer' : 'answers'} from other RFIs`
                }) : undefined,
                title: "Past answers",
                titleSize: "ui"
            }),
            unavailable ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.rfiWorkspace.pastAnswersRetry,
                    onClick: ()=>void past.refetch(),
                    size: "sm",
                    variant: "secondary",
                    children: "Try again"
                }),
                tone: "neutral",
                children: "Past answers could not load. Try again."
            }) : null,
            !unavailable && withMatches.length === 0 && (library.data ?? []).length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                description: "Past answers appear once answers elsewhere are final.",
                framed: true,
                title: "No past answers yet"
            }) : null,
            withMatches.map((r)=>{
                const n = numberOf(r.questionSectionId);
                return /*#__PURE__*/ _jsxs("section", {
                    className: "flex flex-col gap-[var(--space-2)]",
                    "data-testid": Testids.rfiWorkspace.pastAnswersQuestion,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            title: n > 0 ? `Question ${n}` : 'Question',
                            variant: "group"
                        }),
                        r.matches.map((m)=>/*#__PURE__*/ _jsx(SpecraSourceCard, {
                                actions: /*#__PURE__*/ _jsx(SpecraButton, {
                                    asChild: true,
                                    size: "xs",
                                    variant: "secondary",
                                    children: /*#__PURE__*/ _jsx(Link, {
                                        "data-testid": Testids.rfiWorkspace.pastAnswersOpen,
                                        to: rfiQuestionHref(m.rfiResponseProjectId, m.sectionId),
                                        children: "Open"
                                    })
                                }),
                                "data-testid": Testids.rfiWorkspace.pastAnswersCard,
                                description: m.snippet,
                                kind: /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "info",
                                    children: "Past answer"
                                }),
                                title: `${m.rfiTitle} · answer ${m.questionNumber} (${m.pct}% similar)`
                            }, m.sectionId))
                    ]
                }, r.questionSectionId);
            }),
            (library.data ?? []).length > 0 ? /*#__PURE__*/ _jsxs("section", {
                className: "flex flex-col gap-[var(--space-2)]",
                "data-testid": Testids.rfiWorkspace.pastAnswersLibrary,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        title: "From the Library",
                        variant: "group"
                    }),
                    (library.data ?? []).map((item)=>/*#__PURE__*/ _jsx(SpecraSourceCard, {
                            description: item.summary,
                            kind: /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "neutral",
                                children: "Narrative"
                            }),
                            title: item.title
                        }, item.id))
                ]
            }) : null
        ]
    });
}
